import React from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

interface CustomAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  content?: React.ReactNode;
  type?: 'success' | 'error' | 'info' | 'warning';
  showCancelButton?: boolean;
  onConfirm?: () => void;
  confirmText?: React.ReactNode;
  cancelText?: React.ReactNode;
  /** Optional icon shown in the confirm button (left of text). */
  confirmIcon?: React.ReactNode;
  /** Optional icon shown in the cancel button (left of text). Defaults to an X when cancel is shown. */
  cancelIcon?: React.ReactNode;
  themeColor?: string; // optional brand color to match business theme
}

const CustomAlertModal: React.FC<CustomAlertModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  content,
  type = 'info',
  showCancelButton = false,
  onConfirm,
  confirmText = 'OK',
  cancelText = 'Cancel',
  confirmIcon,
  cancelIcon,
  themeColor
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    if (themeColor) {
      // Use themed icon color when provided
      const style = { color: themeColor };
      if (type === 'success') return <CheckCircle className="w-6 h-6" style={style} />;
      if (type === 'error') return <AlertCircle className="w-6 h-6" style={style} />;
      if (type === 'warning') return <AlertCircle className="w-6 h-6" style={style} />;
      return <Info className="w-6 h-6" style={style} />;
    }
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-yellow-600" />;
      default:
        return <Info className="w-6 h-6 text-blue-600" />;
    }
  };

  const getIconBgColor = () => {
    if (themeColor) {
      // Hex with alpha for subtle background
      return '';
    }
    switch (type) {
      case 'success':
        return 'bg-green-100';
      case 'error':
        return 'bg-red-100';
      case 'warning':
        return 'bg-yellow-100';
      default:
        return 'bg-blue-100';
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const getConfirmIcon = () => {
    if (confirmIcon) return confirmIcon;
    // Default: show a small semantic icon on the confirm button.
    if (type === 'success') return <CheckCircle className="w-4 h-4" />;
    if (type === 'error') return <AlertCircle className="w-4 h-4" />;
    if (type === 'warning') return <AlertCircle className="w-4 h-4" />;
    if (type === 'info') return <Info className="w-4 h-4" />;
    return null;
  };

  const getCancelIcon = () => {
    if (!showCancelButton) return null;
    if (cancelIcon !== undefined) return cancelIcon; // allow explicit null to hide
    return <X className="w-4 h-4" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              <div
                className={`w-12 h-12 ${getIconBgColor()} rounded-full flex items-center justify-center`}
                style={themeColor ? { backgroundColor: `${themeColor}1A` } : undefined}
              >
                {getIcon()}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            </div>
          </div>
          
          <div className="mb-6">
            {content ? content : <p className="text-gray-700 whitespace-pre-line">{message}</p>}
          </div>
          
          <div
            className={`flex items-center ${showCancelButton ? 'space-x-3 justify-end' : 'w-full justify-center'}`}
          >
            {showCancelButton && (
              <button
                onClick={onClose}
                className={
                  themeColor
                    ? 'px-4 py-2 rounded-lg transition-colors border hover:opacity-90 inline-flex items-center gap-2'
                    : 'px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors inline-flex items-center gap-2'
                }
                style={
                  themeColor
                    ? { border: `1px solid ${themeColor}`, color: themeColor, backgroundColor: 'transparent' }
                    : undefined
                }
              >
                {getCancelIcon()}
                {cancelText}
              </button>
            )}
            <button
              onClick={handleConfirm}
              className={
                themeColor
                  ? `px-4 py-2 text-white rounded-lg transition-colors inline-flex items-center gap-2 hover:opacity-90 ${
                      showCancelButton ? '' : 'w-full sm:w-auto justify-center'
                    }`
                  : `px-4 py-2 text-white rounded-lg transition-colors inline-flex items-center gap-2 ${
                      showCancelButton ? '' : 'w-full sm:w-auto justify-center'
                    } ${
                      type === 'error'
                        ? 'bg-red-600 hover:bg-red-700'
                        : type === 'success'
                          ? 'bg-green-600 hover:bg-green-700'
                          : type === 'warning'
                            ? 'bg-yellow-600 hover:bg-yellow-700'
                            : 'bg-blue-600 hover:bg-blue-700'
                    }`
              }
              style={themeColor ? { backgroundColor: themeColor } : undefined}
            >
              {getConfirmIcon()}
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomAlertModal;
